const AUTHOR_MARKER = 'wlb-author';
const SOURCE_MARKER = 'wlb-reshare-source';
const LINEAGE_MARKER = 'wlb-reshare';
const COMPACT_LINEAGE_MARKER = 'wlb-r';

export interface ReshareIdentity {
  userGuid?: string | null;
  username: string;
}

export interface ReshareLineage {
  originalAuthor: ReshareIdentity;
  sharers: ReshareIdentity[];
  thoughts: string;
  originalText: string;
  sourceUrl: string;
}

interface LegacyReshareLineageMetadata {
  v: 1;
  original: [string, string];
  sharers: Array<[string, string]>;
}

const normalizeIdentity = (
  identity: ReshareIdentity,
): ReshareIdentity | null => {
  const username = identity.username.trim().replace(/^@/, '');
  if (!username) return null;
  return {
    userGuid: identity.userGuid?.trim() || null,
    username,
  };
};

export const appendReshareIdentity = (
  identities: ReshareIdentity[],
  identity: ReshareIdentity,
): ReshareIdentity[] => {
  const normalized = normalizeIdentity(identity);
  if (!normalized) return identities;
  const key = normalized.userGuid?.toLowerCase()
    || normalized.username.toLowerCase();
  if (
    identities.some(item =>
      (item.userGuid?.toLowerCase() || item.username.toLowerCase()) === key,
    )
  ) {
    return identities;
  }
  return [...identities, normalized];
};

export const buildReshareSourceUrl = (
  shareUrl: string,
  authorGuid?: string | null,
): string => {
  try {
    const url = new URL(shareUrl);
    url.hash = authorGuid?.trim()
      ? `${AUTHOR_MARKER}=${encodeURIComponent(authorGuid.trim())}`
      : SOURCE_MARKER;
    return url.toString();
  } catch {
    return shareUrl;
  }
};

export const buildReshareLineageUrl = (
  shareUrl: string,
  originalAuthor: ReshareIdentity,
  sharers: ReshareIdentity[],
): string => {
  const normalizedOriginal = normalizeIdentity(originalAuthor);
  if (!normalizedOriginal) return buildReshareSourceUrl(shareUrl);

  const normalizedSharers = sharers.reduce<ReshareIdentity[]>(
    appendReshareIdentity,
    [],
  );
  const fields = [
    normalizedOriginal.userGuid || '',
    normalizedOriginal.username,
    ...normalizedSharers.flatMap(sharer => [
      sharer.userGuid || '',
      sharer.username,
    ]),
  ];

  try {
    const url = new URL(shareUrl);
    url.hash = `${COMPACT_LINEAGE_MARKER}=${fields
      .map(field => encodeURIComponent(field))
      .join('~')}`;
    return url.toString();
  } catch {
    return shareUrl;
  }
};

/**
 * Reads the original author identity persisted in a reshare source URL.
 * The closest preceding @ token supplies the display name; the fragment
 * supplies the stable profile GUID used by MentionText navigation.
 */
export const extractReshareAuthor = (
  text: string,
): ReshareIdentity | null => {
  const lineage = extractReshareLineage(text);
  if (lineage) return lineage.originalAuthor;

  const marker = text.match(
    new RegExp(`https?:\\/\\/[^\\s#]+#${AUTHOR_MARKER}=([^\\s&]+)`, 'i'),
  );
  if (marker?.index == null || !marker[1]) return null;

  const precedingText = text.slice(0, marker.index);
  const mentions = Array.from(
    precedingText.matchAll(/@([a-zA-Z0-9_.]+)/g),
  );
  const username = mentions.at(-1)?.[1];
  if (!username) return null;

  try {
    return {
      userGuid: decodeURIComponent(marker[1]),
      username,
    };
  } catch {
    return null;
  }
};

export const extractReshareLineage = (
  text: string,
): ReshareLineage | null => {
  const compactMarker = text.match(
    new RegExp(
      `(https?:\\/\\/[^\\s#]+)#${COMPACT_LINEAGE_MARKER}=([^\\s<]+)`,
      'i',
    ),
  );
  if (
    compactMarker?.index != null
    && compactMarker[1]
    && compactMarker[2]
  ) {
    try {
      const fields = compactMarker[2]
        .split('~')
        .map(field => decodeURIComponent(field));
      if (fields.length < 2) return null;
      const originalAuthor = normalizeIdentity({
        userGuid: fields[0],
        username: fields[1] || '',
      });
      if (!originalAuthor) return null;

      let sharers: ReshareIdentity[] = [];
      for (let index = 2; index + 1 < fields.length; index += 2) {
        sharers = appendReshareIdentity(sharers, {
          userGuid: fields[index],
          username: fields[index + 1] || '',
        });
      }

      return buildExtractedLineage(
        text,
        compactMarker.index,
        compactMarker[1],
        originalAuthor,
        sharers,
      );
    } catch {
      return null;
    }
  }

  const marker = text.match(
    new RegExp(
      `(https?:\\/\\/[^\\s#]+)#${LINEAGE_MARKER}=([^\\s]+)`,
      'i',
    ),
  );
  if (marker?.index == null || !marker[1] || !marker[2]) {
    const legacyMarker = text.match(
      new RegExp(
        `(https?:\\/\\/[^\\s#]+)#${AUTHOR_MARKER}=([^\\s&]+)`,
        'i',
      ),
    );
    if (legacyMarker?.index == null || !legacyMarker[1] || !legacyMarker[2]) {
      return null;
    }

    try {
      const precedingLines = text
        .slice(0, legacyMarker.index)
        .trimEnd()
        .split('\n');
      let attributionIndex = -1;
      let username = '';
      for (let index = precedingLines.length - 1; index >= 0; index -= 1) {
        const attribution = precedingLines[index]?.trim().match(
          /^Shared from @([a-zA-Z0-9_.]+)$/i,
        );
        if (attribution?.[1]) {
          attributionIndex = index;
          username = attribution[1];
          break;
        }
      }
      if (attributionIndex < 0 || !username) return null;

      return {
        originalAuthor: {
          userGuid: decodeURIComponent(legacyMarker[2]),
          username,
        },
        sharers: [],
        thoughts: precedingLines.slice(0, attributionIndex).join('\n').trim(),
        originalText: precedingLines
          .slice(attributionIndex + 1)
          .join('\n')
          .trim(),
        sourceUrl: legacyMarker[1],
      };
    } catch {
      return null;
    }
  }

  try {
    const metadata = JSON.parse(
      decodeURIComponent(marker[2]),
    ) as LegacyReshareLineageMetadata;
    if (
      metadata.v !== 1
      || !Array.isArray(metadata.original)
      || !Array.isArray(metadata.sharers)
    ) {
      return null;
    }

    const originalAuthor = normalizeIdentity({
      userGuid: metadata.original[0],
      username: metadata.original[1],
    });
    if (!originalAuthor) return null;

    const sharers = metadata.sharers.reduce<ReshareIdentity[]>(
      (items, [userGuid, username]) =>
        appendReshareIdentity(items, { userGuid, username }),
      [],
    );
    return buildExtractedLineage(
      text,
      marker.index,
      marker[1],
      originalAuthor,
      sharers,
    );
  } catch {
    const precedingLines = text.slice(0, marker.index).trimEnd().split('\n');
    for (let index = precedingLines.length - 1; index >= 0; index -= 1) {
      const attribution = precedingLines[index]?.trim().match(
        /^Original post by @([a-zA-Z0-9_.]+)$/i,
      );
      if (!attribution?.[1]) continue;
      return {
        originalAuthor: { username: attribution[1] },
        sharers: [],
        thoughts: precedingLines.slice(0, index).join('\n').trim(),
        originalText: precedingLines.slice(index + 1).join('\n').trim(),
        sourceUrl: marker[1],
      };
    }
    return null;
  }
};

function buildExtractedLineage(
  text: string,
  markerIndex: number,
  sourceUrl: string,
  originalAuthor: ReshareIdentity,
  sharers: ReshareIdentity[],
): ReshareLineage {
  const precedingLines = text.slice(0, markerIndex).trimEnd().split('\n');
  const originalAttribution = `original post by @${originalAuthor.username}`
    .toLowerCase();
  let attributionIndex = -1;
  for (let index = precedingLines.length - 1; index >= 0; index -= 1) {
    if (precedingLines[index]?.trim().toLowerCase() === originalAttribution) {
      attributionIndex = index;
      break;
    }
  }

  return {
    originalAuthor,
    sharers,
    thoughts: (
      attributionIndex >= 0
        ? precedingLines.slice(0, attributionIndex)
        : precedingLines
    ).join('\n').trim(),
    originalText: (
      attributionIndex >= 0
        ? precedingLines.slice(attributionIndex + 1)
        : []
    ).join('\n').trim(),
    sourceUrl,
  };
}

export const extractReshareSourcePostId = (
  lineage: ReshareLineage | null,
): string | null => {
  if (!lineage) return null;
  try {
    const pathParts = new URL(lineage.sourceUrl).pathname
      .split('/')
      .filter(Boolean);
    const postIndex = pathParts.findIndex(
      part => part.toLowerCase() === 'post',
    );
    const postId = postIndex >= 0 ? pathParts[postIndex + 1] : null;
    return postId && /^\d+$/.test(postId) ? postId : null;
  } catch {
    return null;
  }
};

export const stripReshareMetadataFromText = (text: string): string =>
  text
    .replace(
      new RegExp(
        `https?:\\/\\/[^\\s<]+#(?:${AUTHOR_MARKER}=|${LINEAGE_MARKER}=|${COMPACT_LINEAGE_MARKER}=|${SOURCE_MARKER})[^\\s<]*`,
        'gi',
      ),
      '',
    )
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const stripReshareCreditLine = (text: string): string =>
  text
    .split(/\r?\n/)
    .filter(
      line =>
        !/^\s*(?:Original post by|Shared from)\s+@[a-zA-Z0-9_.]+\s*$/i.test(
          line,
        ),
    )
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/** Hides internal attribution metadata from the visible/clickable post URL. */
export const stripReshareAuthorMarker = (url: string): string =>
  url.replace(
    new RegExp(
      `#(?:${AUTHOR_MARKER}=[^\\s]+|${LINEAGE_MARKER}=[^\\s]+|${COMPACT_LINEAGE_MARKER}=[^\\s]+)$`,
      'i',
    ),
    '',
  );

export const isInternalReshareSourceUrl = (url: string): boolean =>
  new RegExp(
    `#(?:${AUTHOR_MARKER}=|${LINEAGE_MARKER}=|${COMPACT_LINEAGE_MARKER}=|${SOURCE_MARKER})`,
    'i',
  ).test(url);
